import VariableChip from '@/components/common/chips/VariableChip';
import VariablePicker from '@/components/nodes/settings/variable/VariablePicker';
import { useMetadata } from '@/hooks/useMetadata';
import { Application } from '@/types/application';
import { Prompt } from '@/types/prompt';
import { isString, isVariable } from '@/util/typeGuards';
import { getScopeType, getVariableTypeLabel } from '@/util/variables';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';

type PromptEditorProps = {
  open: boolean;
  onClose: () => void;
  application: Application;
  prompt: Prompt;
  onSave: (prompt: Prompt) => void;
};

const PromptEditor: FC<PromptEditorProps> = (props) => {
  const { metadata } = useMetadata();
  const [editedPrompt, setEditedPrompt] = useState<Prompt>(props.prompt);
  const [openVars, setOpenVars] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    switch (event.currentTarget.id) {
      case 'prompt.label':
        setEditedPrompt({
          ...editedPrompt,
          label: event.currentTarget.value
        });
        break;
      case 'prompt.file':
        setEditedPrompt({
          ...editedPrompt,
          file: event.currentTarget.value
        });
        break;
      case 'prompt.path':
        setEditedPrompt({
          ...editedPrompt,
          path: event.currentTarget.value
        });
        break;

      default:
        break;
    }
  };

  const handleClose = () => {
    props.onClose();
  };

  useEffect(() => {
    setEditedPrompt(props.prompt);
  }, [props.open]);

  const variableType = isVariable(editedPrompt.path)
    ? getVariableTypeLabel(editedPrompt.path, props.application, metadata)
    : undefined;

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth='xs'>
      <DialogTitle>{props.prompt?.file ? 'Edit' : 'Create'}</DialogTitle>
      <DialogContent>
        <Stack direction='column' gap={2} pt={1}>
          <TextField
            id='prompt.label'
            label='Label'
            defaultValue={props.prompt?.label}
            onChange={handleChange}
            InputProps={{ autoComplete: 'off' }}
          />
          {!editedPrompt.promptGroup && (
            <Box display={'flex'} gap={2}>
              <TextField
                id='prompt.path'
                label='Path'
                defaultValue={props.prompt?.path}
                value={isString(editedPrompt.path) ? editedPrompt.path : ''}
                onChange={handleChange}
                InputProps={{ autoComplete: 'off' }}
              />
              <VariableChip
                onClick={() => setOpenVars(true)}
                onDelete={() =>
                  setEditedPrompt({
                    ...editedPrompt,
                    path: ''
                  })
                }
                type={undefined}
                variableType={variableType}
                value={!isString(editedPrompt.path) ? editedPrompt.path : undefined}
                chipText={!isString(editedPrompt.path) ? editedPrompt.path?.name : undefined}
                scope={!isString(editedPrompt.path) ? editedPrompt.path?.scope : undefined}
              />
              <VariablePicker
                subroutineId={1}
                open={openVars}
                onClose={() => setOpenVars(false)}
                type={['string']}
                scope={getScopeType('promptgroups', metadata)}
                onVariablePicked={(variable) =>
                  setEditedPrompt({
                    ...editedPrompt,
                    path: variable
                  })
                }
              />
            </Box>
          )}
          <TextField
            id='prompt.file'
            label='File'
            defaultValue={props.prompt?.file}
            onChange={handleChange}
            required
            InputProps={{ autoComplete: 'off' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          Cancel
        </Button>
        <Button
          disabled={!editedPrompt.file}
          onClick={() => {
            props.onSave({ ...editedPrompt });
            handleClose();
          }}
          color='primary'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptEditor;
